<template>
  <div class="container-full">
    <div class="site-content">
      <div class="main-content main-learner-content">
        <!-- Return to LMS Button -->
        <div class="return-button-container">
          <button
            v-if="returnUrl"
            class="btn primary medium"
            @click="returnToPlatform"
          >
            Return to LMS
          </button>
          <button
            v-else
            class="btn medium disabled"
            disabled
            title="No return URL provided"
          >
            Return to LMS
          </button>
          <p v-if="!returnUrl" class="no-return-url-message">
            No return URL was provided
          </p>
        </div>

        <!-- Student Info -->
        <div class="main-photoUser" v-if="studentName">
          <div class="main-photoUserContent">
            <h4>{{ studentName }}</h4>
            <p>Student Feedback Review</p>
          </div>
        </div>

        <!-- Module Title -->
        <div class="main-title" v-if="moduleName">
          <h3>{{ moduleName }}</h3>
        </div>

        <!-- Overall Grade Display -->
        <div class="modal-grade" v-if="showGrade && overallGradePercentage !== null">
          <p>
            Overall:
            <span class="grade-percentage">{{ overallGradePercentage }}%</span>
            <span v-if="overallGradeLabel" class="grade-label"> - {{ overallGradeLabel }}</span>
          </p>
        </div>

        <!-- Final Feedback Component (reused from learner view) -->
        <div v-if="feedbackData && feedbackData.data">
          <final-feedback :finalDebrief="feedbackData" />
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" class="loading-container">
          <p>Loading feedback...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <p class="error-message">{{ error }}</p>
        </div>

        <!-- No Data State -->
        <div v-else-if="!loading && (!feedbackData || !feedbackData.data)" class="no-data-container">
          <p>No feedback available. The student may not have completed this assignment yet.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../services/api";
import finalFeedback from "../components/finalFeedback";
import { utilFunctionService } from "@/utils/utils.service";

export default {
  name: "learnerFeedbackView",
  components: {
    finalFeedback,
  },
  data() {
    return {
      feedbackData: null,
      studentName: "",
      moduleName: "",
      overallGradePercentage: null,
      overallGradeLabel: "",
      showGrade: true,
      returnUrl: null,
      loading: true,
      error: null,
    };
  },
  created() {
    this.loadFeedbackData();
  },
  methods: {
    async loadFeedbackData() {
      const userSectionId = this.$route.params.userSectionId;

      if (!userSectionId) {
        this.error = "No user section ID provided";
        this.loading = false;
        return;
      }

      // Read return URL before the API call so it's available even if the request fails.
      // lti_return_url is stored JSON-stringified via setLocalStorageService, so use
      // getLocalStorageService to parse it back to a plain string consistently.
      const storedReturnUrl = utilFunctionService.getLocalStorageService("lti_return_url");
      if (typeof storedReturnUrl === 'string' && storedReturnUrl) {
        try {
          const parsed = new URL(storedReturnUrl);
          if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
            this.returnUrl = storedReturnUrl;
          }
        } catch {
          // Invalid URL, leave returnUrl null
        }
      }

      utilFunctionService.showLoader();

      try {
        const response = await api.getLearnerFeedback(userSectionId);

        if (response && response.data) {
          this.feedbackData = {
            data: response.data.data,
          };

          this.studentName = `${response.data.user_first_name || ""} ${response.data.user_last_name || ""}`.trim();
          this.moduleName = response.data.learn_mod_name;
          this.overallGradePercentage = response.data.overall_grade_percentage;
          this.overallGradeLabel = response.data.overall_grade_label;
          this.showGrade = response.data.enable_grading !== false;
          this.loading = false;
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        if (err.response && err.response.status === 403) {
          this.error = "You do not have permission to view this student's feedback";
        } else if (err.response && err.response.status === 404) {
          this.error = "Feedback not found for this submission";
        } else if (err.response && err.response.status === 422) {
          this.error = "This student has not completed the assignment yet";
        } else {
          this.error = "Failed to load feedback. Please try again later.";
        }

        this.loading = false;
      } finally {
        utilFunctionService.hideLoader();
      }
    },
    returnToPlatform() {
      if (this.returnUrl) {
        window.location.href = this.returnUrl;
      }
    },
  },
};
</script>

<style scoped>
.modal-grade {
  background-color: #f5f5f5;
  padding: 24px 32px;
  margin: 30px 0;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.modal-grade p {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.grade-percentage {
  color: #4CAF50;
  font-size: 32px;
  font-weight: 700;
}

.grade-label {
  color: #666;
  font-size: 18px;
  font-weight: normal;
}

.loading-container,
.error-container,
.no-data-container {
  padding: 40px;
  text-align: center;
}

.error-message {
  color: #d32f2f;
  font-size: 16px;
}

.main-photoUser {
  margin-bottom: 24px;
}

.main-photoUserContent h4 {
  font-size: 24px;
  margin-bottom: 5px;
}

.main-photoUserContent p {
  color: #666;
  font-size: 14px;
}

.main-title {
  margin: 24px 0;
}

.main-title h3 {
  font-size: 28px;
  color: #333;
}

.return-button-container {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.no-return-url-message {
  color: #666;
  font-size: 14px;
  font-style: italic;
  margin: 0;
}

.btn.disabled {
  background-color: #e0e0e0;
  color: #999;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Override global dashboard styles for full-width learner feedback view */
.site-content {
  padding-top: 0 !important;
}

.main-content.main-learner-content {
  width: 100% !important;
  max-width: 100% !important;
  padding: 30px;
}
</style>
